import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type DragEvent,
  type FormEvent,
} from "react";
import { useSearchParams } from "react-router";
import { Icon } from "../components/Icon.tsx";
import { beforeFor, Reorder } from "../components/Reorder.tsx";
import { Shell } from "../components/Shell.tsx";
import {
  Button,
  Confirm,
  Dialog,
  Field,
  IconButton,
  Skeleton,
  Switch,
} from "../components/ui.tsx";
import { t, tn, type MessageKey } from "../i18n/index.ts";
import { action, subscribe } from "../lib/api.ts";
import { day as dayText } from "../lib/format.ts";
import { useUi } from "../lib/ui-state.tsx";
import { useAction, useSave } from "./manage/common.tsx";

/**
 * To-do: my own board of sticky notes. Columns are mine to name; a to-do can carry notes, a
 * checklist, a day, a star and tags, and be shared with colleagues, who see it on their own board.
 */

const TONES = [
  "blue",
  "green",
  "orange",
  "purple",
  "red",
  "teal",
  "yellow",
  "grey",
] as const;
type ToneName = (typeof TONES)[number];

interface Stage {
  id: string;
  name: string;
}
interface TagLite {
  id: string;
  name: string;
  tone: ToneName;
}
interface Card {
  id: string;
  title: string;
  notes: string | null;
  owner: string;
  owner_name: string;
  due: string | null;
  starred: boolean;
  done_at: string | null;
  stage: string;
  seen: boolean;
  items: number;
  items_done: number;
  tags: TagLite[];
  shared_with: { id: string; name: string }[];
}
interface Board {
  today: string;
  stages: Stage[];
  tasks: Card[];
  tags: TagLite[];
  counts: { open: number; overdue: number; today: number; new: number };
}
interface Full {
  id: string;
  title: string;
  notes: string | null;
  owner: string;
  due: string | null;
  starred: boolean;
  done_at: string | null;
  stage: string | null;
  items: { id: string; text: string; done: boolean }[];
  members: { id: string; name: string }[];
  tags: string[];
  mine: boolean;
}

const initials = (name: string) =>
  name
    .split(/\s+/)
    .map((p) => p[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

export function Todo() {
  const [params, setParams] = useSearchParams();
  const showDone = params.get("done") === "1";
  const { data: b, reload } = useAction<Board>("todo.board", {
    done: showDone,
  });
  const { fail } = useUi();
  const again = useRef(reload);
  again.current = reload;
  useEffect(
    () => subscribe(["todo"], (e) => e !== "ready" && void again.current()),
    [],
  );
  const [open, setOpen] = useState<string | "new" | null>(null);
  const [newIn, setNewIn] = useState<string | null>(null);
  const [columns, setColumns] = useState(false);
  const [tagsOpen, setTagsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const tag = params.get("tag");
  // On a phone one column shows at a time.
  const [col, setCol] = useState<string | null>(null);
  const [dragging, setDragging] = useState<string | null>(null);
  const [over, setOver] = useState<{
    stage: string;
    before: string | null;
  } | null>(null);
  const setParam = (k: string, v: string | null) => {
    const p = new URLSearchParams(params);
    if (v) p.set(k, v);
    else p.delete(k);
    setParams(p, { replace: true });
  };
  const visible = useMemo(() => {
    if (!b) return [];
    const q = search.trim().toLowerCase();
    return b.tasks.filter(
      (c) =>
        (!q ||
          c.title.toLowerCase().includes(q) ||
          c.notes?.toLowerCase().includes(q)) &&
        (!tag || c.tags.some((g) => g.id === tag)),
    );
  }, [b, search, tag]);
  const act = async (name: string, input: Record<string, unknown>) => {
    try {
      await action(name, input);
      await reload();
    } catch (e) {
      fail(e);
    }
  };
  const drop = (e: DragEvent, stage: string, before: string | null) => {
    e.preventDefault();
    const id = e.dataTransfer.getData("text/todo") || dragging;
    setDragging(null);
    setOver(null);
    if (id && id !== before) void act("todo.move", { id, stage, before });
  };
  const current = b
    ? (b.stages.find((s) => s.id === col) ?? b.stages[0]!)
    : null;
  return (
    <Shell>
      <div className="page td-page">
        <div className="page-head">
          <div>
            <h1>{t("todo.title")}</h1>
            <p className="muted">
              {b
                ? [
                    tn("todo.open", b.counts.open),
                    b.counts.overdue
                      ? tn("todo.overdue", b.counts.overdue)
                      : null,
                    b.counts.today
                      ? tn("todo.due_today", b.counts.today)
                      : null,
                    b.counts.new ? tn("todo.new_shared", b.counts.new) : null,
                  ]
                    .filter(Boolean)
                    .join(" · ")
                : " "}
            </p>
          </div>
          <Button variant="primary" icon="plus" onClick={() => setOpen("new")}>
            {t("todo.add")}
          </Button>
        </div>
        <div className="td-toolbar">
          <label className="td-search">
            <Icon name="search" size={16} />
            <span className="sr-only">{t("todo.search")}</span>
            <input
              className="input"
              type="search"
              placeholder={t("todo.search")}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </label>
          {b?.tags.length ? (
            <div
              className="td-tagfilter"
              role="group"
              aria-label={t("todo.filter_tag")}
            >
              {b.tags.map((g) => (
                <button
                  key={g.id}
                  type="button"
                  className={`td-tag tone-${g.tone}${tag === g.id ? " on" : ""}`}
                  aria-pressed={tag === g.id}
                  onClick={() => setParam("tag", tag === g.id ? null : g.id)}
                >
                  {g.name}
                </button>
              ))}
            </div>
          ) : null}
          <span className="td-spacer" />
          <label className="switch-row td-done-switch">
            <span>{t("todo.show_all_done")}</span>
            <Switch
              checked={showDone}
              label={t("todo.show_all_done")}
              onChange={(v) => setParam("done", v ? "1" : null)}
            />
          </label>
          <Button
            size="sm"
            variant="ghost"
            icon="bookmark"
            onClick={() => setTagsOpen(true)}
          >
            {t("todo.tags")}
          </Button>
          <Button
            size="sm"
            variant="ghost"
            icon="layout"
            onClick={() => setColumns(true)}
          >
            {t("todo.columns")}
          </Button>
        </div>
        {!b || !current ? (
          <Skeleton h={360} r="var(--radius-lg)" />
        ) : (
          <>
            <div
              className="segmented tabs td-coltabs"
              role="tablist"
              aria-label={t("todo.columns")}
            >
              {b.stages.map((s) => {
                const n = visible.filter(
                  (c) => c.stage === s.id && !c.done_at,
                ).length;
                return (
                  <button
                    key={s.id}
                    role="tab"
                    aria-selected={current.id === s.id}
                    className={current.id === s.id ? "active" : ""}
                    onClick={() => setCol(s.id)}
                  >
                    {s.name}
                    {n ? <span className="td-count">{n}</span> : null}
                  </button>
                );
              })}
            </div>
            <div className="td-board">
              {b.stages.map((s) => {
                const cards = visible.filter((c) => c.stage === s.id);
                const openCards = cards.filter((c) => !c.done_at);
                const doneCards = cards.filter((c) => c.done_at);
                return (
                  <section
                    key={s.id}
                    className={`td-col${current.id === s.id ? " current" : ""}${over?.stage === s.id && !over.before ? " over" : ""}`}
                    aria-label={s.name}
                    onDragOver={(e) => {
                      if (!dragging) return;
                      e.preventDefault();
                      if (over?.stage !== s.id || over.before)
                        setOver({ stage: s.id, before: null });
                    }}
                    onDrop={(e) => drop(e, s.id, null)}
                  >
                    <header className="td-col-head">
                      <h2>{s.name}</h2>
                      <span className="muted small">{openCards.length}</span>
                    </header>
                    <QuickAdd
                      stage={s.id}
                      onAdded={reload}
                      onMore={() => (setNewIn(s.id), setOpen("new"))}
                    />
                    {!cards.length ? (
                      <p className="td-empty muted small">
                        {search || tag
                          ? t("todo.nothing_matches")
                          : t("todo.col_empty")}
                      </p>
                    ) : (
                      <ul className="td-cards">
                        {[...openCards, ...doneCards].map((c) => (
                          <li
                            key={c.id}
                            className={
                              over?.before === c.id
                                ? "td-drop-before"
                                : undefined
                            }
                            onDragOver={(e) => {
                              if (!dragging || dragging === c.id) return;
                              e.preventDefault();
                              e.stopPropagation();
                              if (over?.before !== c.id)
                                setOver({ stage: s.id, before: c.id });
                            }}
                            onDrop={(e) => {
                              e.stopPropagation();
                              drop(e, s.id, c.id);
                            }}
                          >
                            <TodoCard
                              c={c}
                              today={b.today}
                              dragging={dragging === c.id}
                              onOpen={() => setOpen(c.id)}
                              onDone={(done) =>
                                void act("todo.set_done", { id: c.id, done })
                              }
                              onStar={() =>
                                void act("todo.star", {
                                  id: c.id,
                                  starred: !c.starred,
                                })
                              }
                              onDragStart={(e) => {
                                e.dataTransfer.setData("text/todo", c.id);
                                e.dataTransfer.effectAllowed = "move";
                                setDragging(c.id);
                              }}
                              onDragEnd={() => {
                                setDragging(null);
                                setOver(null);
                              }}
                            />
                          </li>
                        ))}
                      </ul>
                    )}
                  </section>
                );
              })}
            </div>
            {!b.tasks.length && !showDone ? (
              <p className="muted small td-hint">{t("todo.hint")}</p>
            ) : null}
          </>
        )}
      </div>
      {open && b ? (
        <TodoDialog
          id={open === "new" ? null : open}
          board={b}
          stage={newIn}
          onClose={() => {
            setOpen(null);
            setNewIn(null);
          }}
          onSaved={reload}
        />
      ) : null}
      {columns && b ? (
        <ColumnsDialog
          stages={b.stages}
          onClose={() => setColumns(false)}
          onSaved={reload}
        />
      ) : null}
      {tagsOpen && b ? (
        <TagsDialog
          tags={b.tags}
          onClose={() => setTagsOpen(false)}
          onSaved={reload}
        />
      ) : null}
    </Shell>
  );
}

function QuickAdd({
  stage,
  onAdded,
  onMore,
}: {
  stage: string;
  onAdded: () => unknown;
  onMore: () => void;
}) {
  const [v, setV] = useState("");
  const { busy, run } = useSave();
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!v.trim()) return;
    if (
      await run(
        async () => (
          await action("todo.quick_add", { title: v, stage }),
          await onAdded()
        ),
      )
    )
      setV("");
  };
  return (
    <form className="td-quick" onSubmit={(e) => void submit(e)}>
      <input
        className="input"
        value={v}
        maxLength={200}
        placeholder={t("todo.quick_ph")}
        aria-label={t("todo.quick_label")}
        onChange={(e) => setV(e.target.value)}
        disabled={busy}
      />
      <IconButton
        icon="sliders"
        label={t("todo.more_details")}
        onClick={onMore}
      />
    </form>
  );
}

function TodoCard({
  c,
  today,
  dragging,
  onOpen,
  onDone,
  onStar,
  onDragStart,
  onDragEnd,
}: {
  c: Card;
  today: string;
  dragging: boolean;
  onOpen: () => void;
  onDone: (done: boolean) => void;
  onStar: () => void;
  onDragStart: (e: DragEvent) => void;
  onDragEnd: () => void;
}) {
  const late = !c.done_at && c.due && c.due < today;
  const dueText =
    c.due === today ? t("todo.today") : c.due ? dayText(c.due) : null;
  return (
    <article
      className={`td-card${c.done_at ? " done" : ""}${dragging ? " dragging" : ""}${c.starred ? " starred" : ""}`}
      draggable
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
    >
      <label className="td-check">
        <input
          type="checkbox"
          checked={!!c.done_at}
          onChange={(e) => onDone(e.target.checked)}
        />
        <span className="sr-only">
          {c.done_at
            ? t("todo.mark_open", { title: c.title })
            : t("todo.mark_done", { title: c.title })}
        </span>
      </label>
      <button type="button" className="td-card-body" onClick={onOpen}>
        <span className="td-card-title">
          {!c.seen ? <span className="td-new">{t("todo.new")}</span> : null}
          {c.title}
        </span>
        {c.notes ? (
          <span className="td-card-notes muted small">{c.notes}</span>
        ) : null}
        <span className="td-card-meta">
          {dueText ? (
            <span
              className={`td-due${late ? " late" : c.due === today ? " now" : ""}`}
            >
              <Icon name={late ? "alert" : "calendar"} size={14} />
              {late ? t("todo.late", { day: dueText }) : dueText}
            </span>
          ) : null}
          {c.items ? (
            <span
              className={`td-items${c.items_done === c.items ? " all" : ""}`}
            >
              <Icon name="check" size={14} />
              {c.items_done}/{c.items}
            </span>
          ) : null}
          {c.tags.map((g) => (
            <span key={g.id} className={`td-tag small tone-${g.tone}`}>
              {g.name}
            </span>
          ))}
          {c.shared_with.length ? (
            <span
              className="td-people"
              title={c.shared_with.map((p) => p.name).join(", ")}
            >
              {c.shared_with.slice(0, 3).map((p) => (
                <span key={p.id} className="td-avatar" aria-hidden="true">
                  {initials(p.name)}
                </span>
              ))}
              <span className="sr-only">
                {t("todo.shared_with", {
                  names: c.shared_with.map((p) => p.name).join(", "),
                })}
              </span>
            </span>
          ) : null}
        </span>
      </button>
      <button
        type="button"
        className={`td-star${c.starred ? " on" : ""}`}
        aria-pressed={c.starred}
        aria-label={c.starred ? t("todo.unstar") : t("todo.star")}
        title={c.starred ? t("todo.unstar") : t("todo.star")}
        onClick={onStar}
      >
        <Icon name="star" size={16} />
      </button>
    </article>
  );
}

// ---- the editor ---------------------------------------------------------------------------

function TodoDialog({
  id,
  board,
  stage,
  onClose,
  onSaved,
}: {
  id: string | null;
  board: Board;
  stage: string | null;
  onClose: () => void;
  onSaved: () => unknown;
}) {
  const { busy, run } = useSave();
  const [full, setFull] = useState<Full | null>(null);
  const [people, setPeople] = useState<{ id: string; name: string }[]>([]);
  const [f, setF] = useState({
    title: "",
    notes: "",
    due: "",
    starred: false,
    stage: stage ?? board.stages[0]!.id,
    tags: [] as string[],
    share: [] as string[],
  });
  const [items, setItems] = useState<
    { id: string | null; text: string; done: boolean }[]
  >([]);
  const [line, setLine] = useState("");
  const [tried, setTried] = useState(false);
  const [drop, setDrop] = useState(false);
  const [moving, setMoving] = useState(false);
  const { fail } = useUi();
  useEffect(() => {
    void action<{ id: string; name: string }[]>("todo.people", {}).then(
      setPeople,
      () => setPeople([]),
    );
    if (!id) return;
    action<Full>("todo.get", { id }).then((x) => {
      setFull(x);
      setF({
        title: x.title,
        notes: x.notes ?? "",
        due: x.due ?? "",
        starred: x.starred,
        stage: x.stage ?? board.stages[0]!.id,
        tags: x.tags,
        share: x.members.map((m) => m.id),
      });
      setItems(x.items.map((i) => ({ id: i.id, text: i.text, done: i.done })));
    }, fail);
  }, [id]);
  const mine = !id || !!full?.mine;
  const addLine = () => {
    if (!line.trim()) return;
    setItems([...items, { id: null, text: line.trim(), done: false }]);
    setLine("");
  };
  const save = async () => {
    setTried(true);
    if (!f.title.trim()) return;
    const ok = await run(
      async () => {
        await action("todo.save_task", {
          id,
          title: f.title,
          notes: f.notes || null,
          due: f.due || null,
          starred: f.starred,
          stage: f.stage,
          tags: f.tags,
          share: f.share.filter((u) => people.some((p) => p.id === u) || !mine),
          items: [
            ...items,
            ...(line.trim()
              ? [{ id: null, text: line.trim(), done: false }]
              : []),
          ],
        });
        await onSaved();
      },
      id ? t("todo.saved") : t("todo.added"),
    );
    if (ok) onClose();
  };
  if (id && !full) return null;
  const done = items.filter((i) => i.done).length;
  return (
    <Dialog
      title={id ? t("todo.edit") : t("todo.add")}
      size="lg"
      onClose={onClose}
      footer={
        <>
          {id ? (
            <Button
              variant="ghost"
              icon={mine ? "trash" : "logout"}
              className="td-remove"
              onClick={() => setDrop(true)}
            >
              {mine ? t("todo.delete") : t("todo.leave")}
            </Button>
          ) : null}
          <Button variant="ghost" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button
            variant="primary"
            icon="check"
            busy={busy}
            onClick={() => void save()}
          >
            {id ? t("common.save") : t("todo.add_save")}
          </Button>
        </>
      }
    >
      <div className="td-editor">
        <div className="td-editor-main">
          <Field
            label={t("todo.field_title")}
            required
            error={tried && !f.title.trim() ? t("todo.title_needed") : null}
          >
            <input
              className="input td-title-input"
              value={f.title}
              maxLength={200}
              onChange={(e) => setF({ ...f, title: e.target.value })}
              data-autofocus
            />
          </Field>
          <Field label={t("todo.notes")}>
            <textarea
              className="input"
              rows={4}
              value={f.notes}
              maxLength={10_000}
              placeholder={t("todo.notes_ph")}
              onChange={(e) => setF({ ...f, notes: e.target.value })}
            />
          </Field>
          <div className="td-checklist">
            <div className="td-checklist-head">
              <h3>{t("todo.checklist")}</h3>
              {items.length ? (
                <span className="muted small">
                  {t("todo.checklist_done", { done, all: items.length })}
                </span>
              ) : null}
            </div>
            {items.length ? (
              <div
                className="td-progress"
                role="progressbar"
                aria-valuemin={0}
                aria-valuemax={items.length}
                aria-valuenow={done}
                aria-label={t("todo.checklist")}
              >
                <span style={{ width: `${(done / items.length) * 100}%` }} />
              </div>
            ) : null}
            <ul className="td-lines">
              {items.map((i, n) => (
                <li
                  key={i.id ?? `n${n}`}
                  className={i.done ? "done" : undefined}
                >
                  <input
                    type="checkbox"
                    checked={i.done}
                    aria-label={t("todo.line_done", { text: i.text })}
                    onChange={(e) =>
                      setItems(
                        items.map((x, j) =>
                          j === n ? { ...x, done: e.target.checked } : x,
                        ),
                      )
                    }
                  />
                  <input
                    className="input input-plain"
                    value={i.text}
                    maxLength={300}
                    aria-label={t("todo.line_n", { n: n + 1 })}
                    onChange={(e) =>
                      setItems(
                        items.map((x, j) =>
                          j === n ? { ...x, text: e.target.value } : x,
                        ),
                      )
                    }
                  />
                  <IconButton
                    icon="x"
                    label={t("todo.remove_line")}
                    onClick={() => setItems(items.filter((_, j) => j !== n))}
                  />
                </li>
              ))}
            </ul>
            {items.length < 50 ? (
              <div className="td-addline">
                <input
                  className="input"
                  value={line}
                  maxLength={300}
                  placeholder={t("todo.add_line_ph")}
                  aria-label={t("todo.add_line")}
                  onChange={(e) => setLine(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      addLine();
                    }
                  }}
                />
                <Button icon="plus" onClick={addLine} disabled={!line.trim()}>
                  {t("todo.add_line")}
                </Button>
              </div>
            ) : null}
          </div>
        </div>
        <aside className="td-editor-side">
          <Field label={t("todo.column")}>
            <select
              className="input"
              value={f.stage}
              onChange={(e) => setF({ ...f, stage: e.target.value })}
            >
              {board.stages.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          </Field>
          {id
            ? (() => {
                // Its place in the column as the board shows it, movable without dragging.
                const card = board.tasks.find((c) => c.id === id);
                const col = card && !card.done_at ? board.tasks.filter((c) => c.stage === card.stage && !c.done_at).map((c) => c.id) : [];
                const i = col.indexOf(id);
                return (
                  <Reorder
                    index={i}
                    count={col.length}
                    busy={moving}
                    onMove={(by) => {
                      setMoving(true);
                      void action("todo.move", { id, stage: card!.stage, before: beforeFor(col, i, by) })
                        .then(() => onSaved(), fail)
                        .finally(() => setMoving(false));
                    }}
                  />
                );
              })()
            : null}
          <Field label={t("todo.due")}>
            <input
              className="input"
              type="date"
              value={f.due}
              onChange={(e) => setF({ ...f, due: e.target.value })}
            />
          </Field>
          <div className="td-due-quick">
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setF({ ...f, due: board.today })}
            >
              {t("todo.today")}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setF({ ...f, due: plusDays(board.today, 1) })}
            >
              {t("todo.tomorrow")}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setF({ ...f, due: plusDays(board.today, 7) })}
            >
              {t("todo.next_week")}
            </Button>
            {f.due ? (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setF({ ...f, due: "" })}
              >
                {t("todo.no_day")}
              </Button>
            ) : null}
          </div>
          <label className="switch-row">
            <span>{t("todo.starred")}</span>
            <Switch
              checked={f.starred}
              label={t("todo.starred")}
              onChange={(v) => setF({ ...f, starred: v })}
            />
          </label>
          <div className="td-side-block">
            <h3>{t("todo.tags")}</h3>
            {board.tags.length ? (
              <div className="td-tagpick">
                {board.tags.map((g) => {
                  const on = f.tags.includes(g.id);
                  return (
                    <button
                      key={g.id}
                      type="button"
                      className={`td-tag tone-${g.tone}${on ? " on" : ""}`}
                      aria-pressed={on}
                      onClick={() =>
                        setF({
                          ...f,
                          tags: on
                            ? f.tags.filter((x) => x !== g.id)
                            : [...f.tags, g.id],
                        })
                      }
                    >
                      {on ? <Icon name="check" size={12} /> : null}
                      {g.name}
                    </button>
                  );
                })}
              </div>
            ) : (
              <p className="muted small">{t("todo.no_tags")}</p>
            )}
          </div>
          <div className="td-side-block">
            <h3>{t("todo.share")}</h3>
            {mine ? (
              <>
                <p className="muted small">{t("todo.share_lead")}</p>
                <div className="td-sharelist">
                  {people.map((p) => {
                    const on = f.share.includes(p.id);
                    return (
                      <label
                        key={p.id}
                        className={`td-person${on ? " on" : ""}`}
                      >
                        <input
                          type="checkbox"
                          checked={on}
                          onChange={() =>
                            setF({
                              ...f,
                              share: on
                                ? f.share.filter((x) => x !== p.id)
                                : [...f.share, p.id],
                            })
                          }
                        />
                        <span className="td-avatar" aria-hidden="true">
                          {initials(p.name)}
                        </span>
                        {p.name}
                      </label>
                    );
                  })}
                </div>
              </>
            ) : (
              <p className="muted small">
                {t("todo.shared_by", {
                  names: full!.members.map((m) => m.name).join(", "),
                })}
              </p>
            )}
          </div>
        </aside>
      </div>
      {drop && id ? (
        <Confirm
          title={mine ? t("todo.delete_title") : t("todo.leave_title")}
          body={
            mine
              ? f.share.length > 1
                ? t("todo.delete_shared_body")
                : t("todo.delete_body")
              : t("todo.leave_body")
          }
          confirm={mine ? t("todo.delete") : t("todo.leave")}
          danger={mine}
          onClose={() => setDrop(false)}
          onConfirm={() =>
            void run(
              async () => {
                await action("todo.remove", { id });
                await onSaved();
                onClose();
              },
              mine ? t("todo.deleted") : t("todo.left"),
            )
          }
        />
      ) : null}
    </Dialog>
  );
}

function plusDays(day: string, n: number) {
  const d = new Date(`${day}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

// ---- columns and tags -------------------------------------------------------------------

function ColumnsDialog({
  stages,
  onClose,
  onSaved,
}: {
  stages: Stage[];
  onClose: () => void;
  onSaved: () => unknown;
}) {
  const { busy, run } = useSave();
  const [list, setList] = useState(
    stages.map((s) => ({ id: s.id as string | null, name: s.name })),
  );
  const move = (i: number, d: number) => {
    const out = [...list];
    [out[i], out[i + d]] = [out[i + d]!, out[i]!];
    setList(out);
  };
  return (
    <Dialog
      title={t("todo.columns")}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button
            variant="primary"
            icon="check"
            busy={busy}
            disabled={!list.length || list.some((s) => !s.name.trim())}
            onClick={() =>
              void run(async () => {
                await action("todo.save_stages", { stages: list });
                await onSaved();
                onClose();
              }, t("todo.saved"))
            }
          >
            {t("common.save")}
          </Button>
        </>
      }
    >
      <p className="muted small">{t("todo.columns_lead")}</p>
      <ul className="td-collist">
        {list.map((s, i) => (
          <li key={s.id ?? `n${i}`}>
            <input
              className="input"
              value={s.name}
              maxLength={40}
              aria-label={t("todo.column_n", { n: i + 1 })}
              onChange={(e) =>
                setList(
                  list.map((x, j) =>
                    j === i ? { ...x, name: e.target.value } : x,
                  ),
                )
              }
            />
            <IconButton
              icon="up"
              label={t("todo.move_left")}
              disabled={i === 0}
              onClick={() => move(i, -1)}
            />
            <IconButton
              icon="down"
              label={t("todo.move_right")}
              disabled={i === list.length - 1}
              onClick={() => move(i, 1)}
            />
            <IconButton
              icon="trash"
              label={t("todo.remove_column")}
              disabled={list.length === 1}
              onClick={() => setList(list.filter((_, j) => j !== i))}
            />
          </li>
        ))}
      </ul>
      <Button
        icon="plus"
        disabled={list.length >= 12}
        onClick={() => setList([...list, { id: null, name: "" }])}
      >
        {t("todo.add_column")}
      </Button>
    </Dialog>
  );
}

function TagsDialog({
  tags,
  onClose,
  onSaved,
}: {
  tags: TagLite[];
  onClose: () => void;
  onSaved: () => unknown;
}) {
  const { busy, run } = useSave();
  const [name, setName] = useState("");
  const [tone, setTone] = useState<ToneName>("blue");
  const [drop, setDrop] = useState<TagLite | null>(null);
  return (
    <Dialog title={t("todo.tags")} onClose={onClose}>
      <p className="muted small">{t("todo.tags_lead")}</p>
      {tags.length ? (
        <ul className="td-taglist">
          {tags.map((g) => (
            <li key={g.id}>
              <span className={`td-tag tone-${g.tone}`}>{g.name}</span>
              <IconButton
                icon="trash"
                label={t("todo.delete_tag", { name: g.name })}
                onClick={() => setDrop(g)}
              />
            </li>
          ))}
        </ul>
      ) : null}
      <form
        className="td-newtag"
        onSubmit={(e) => {
          e.preventDefault();
          if (!name.trim()) return;
          void run(async () => {
            await action("todo.save_tag", { name, tone });
            setName("");
            await onSaved();
          }, t("todo.tag_added"));
        }}
      >
        <Field label={t("todo.new_tag")}>
          <input
            className="input"
            value={name}
            maxLength={30}
            onChange={(e) => setName(e.target.value)}
          />
        </Field>
        <div
          className="td-tones"
          role="radiogroup"
          aria-label={t("todo.colour")}
        >
          {TONES.map((x) => (
            <button
              key={x}
              type="button"
              role="radio"
              aria-checked={tone === x}
              aria-label={t(`todo.tone_${x}` as MessageKey)}
              className={`td-tone tone-${x}${tone === x ? " on" : ""}`}
              onClick={() => setTone(x)}
            />
          ))}
        </div>
        <Button type="submit" icon="plus" busy={busy} disabled={!name.trim()}>
          {t("todo.add_tag")}
        </Button>
      </form>
      {drop ? (
        <Confirm
          title={t("todo.delete_tag", { name: drop.name })}
          body={t("todo.delete_tag_body")}
          confirm={t("todo.delete")}
          danger
          onClose={() => setDrop(null)}
          onConfirm={() =>
            void run(async () => {
              await action("todo.delete_tag", { id: drop.id });
              setDrop(null);
              await onSaved();
            })
          }
        />
      ) : null}
    </Dialog>
  );
}
